import React from 'react';
import { Link } from 'react-router-dom';

import ReportPDF from '../assets/NebraskaAgTechInnovationReport.pdf';

import AnkitPhoto from '../images/Ankit.png';
import LalPhoto from '../images/Lal.png';
import CostelloPhoto from '../images/Costello.png';

const NebraskaAgTechInnovationReport = () => {
  return (
    <div className="pub-page">

      {/* =====================================================
          BREADCRUMB
          ===================================================== */}

      <div className="pub-breadcrumb">
        <div className="container">
          <Link
            to="/resources"
            className="pub-breadcrumb-link"
          >
            ← Back to Resources
          </Link>
        </div>
      </div>


      {/* =====================================================
          HEADER
          ===================================================== */}

      <section className="pub-header">
        <div className="container">

          <span className="pub-badge">
            WHITE PAPER
          </span>

          <h1 className="pub-title">
            Nebraska and the Midwest AgTech Ecosystem:
            Benchmarking Innovation Investment and Translation Gaps
          </h1>

          <p className="pub-date">
            July 2026 • Spur Ventures Research
          </p>

        </div>
      </section>


      {/* =====================================================
          MAIN BODY
          ===================================================== */}

      <main className="pub-body">

        <div className="container">

          <div className="pub-layout">


            {/* =================================================
                LEFT SIDE — ARTICLE CONTENT
                ================================================= */}

            <article className="pub-main">

              {/* ABSTRACT */}

              <section className="pub-section">

                <h2>
                  Abstract
                </h2>

                <p>
                  Agricultural innovation is central to the sustainable
                  transformation of food systems, yet the capital that
                  finances it is unevenly distributed relative to where
                  food is produced. This paper develops a diagnostic
                  framework for measuring this mismatch and applies it
                  across the U.S. Midwest from 2021 to 2025, with
                  particular emphasis on Nebraska&apos;s structural
                  position within this landscape.
                </p>

                <p>
                  Drawing on venture capital deal-flow data, 17
                  stakeholder interviews, and a novel
                  strength-weakness-opportunity-gap (SWOG) ecosystem
                  mapping methodology, we document $736 million in
                  tracked Midwest AgTech funding across eight states
                  (Nebraska, Iowa, Missouri, Illinois, Indiana, Kansas,
                  North Dakota, and South Dakota).
                </p>

                <p>
                  We introduce three complementary normalized metrics:
                  Agricultural Innovation Investment Ratio (AIIR),
                  Innovation Translation Efficiency (ITE), and Innovation
                  Translation Gap (AITG) anchored by an OLS benchmarking
                  regression (R² = 0.568, p = 0.031) that estimates
                  expected investment from agricultural GDP.
                </p>

                <p>
                  Nebraska&apos;s AIIR of $9.9M per $1B of agricultural
                  output is the third lowest in the sample; its ITE of
                  0.63 indicates the state attracts only 63% of the
                  AgTech VC its agricultural scale structurally predicts;
                  and its AITG of −$45.1M quantifies the institutional
                  investment deficit in dollar terms.
                </p>

                <p>
                  The SWOG analysis documents Nebraska&apos;s durable
                  strengths in cattle and beef systems, irrigation
                  technology, bio-based resources, and university
                  research capacity, while identifying capital formation
                  gaps, weak startup formation pipelines, and fragmented
                  ecosystem coordination as the primary barriers to
                  commercialization.
                </p>

                <p>
                  Comparative analysis reveals that states with smaller
                  agricultural economies—notably North Dakota
                  (ITE = 2.45) and Missouri (ITE = 2.17)—outperform
                  their structural benchmarks through deliberate
                  institutional investment, demonstrating that
                  Nebraska&apos;s gap is closeable.
                </p>

                <p>
                  We conclude with a four-pillar strategic framework for
                  Nebraska to translate agricultural leadership into
                  innovation leadership by 2030.
                </p>

              </section>


              


              {/* =================================================
                  DOWNLOAD SECTION
                  ================================================= */}

              <section className="pub-download-section">

                <div>

                  <span className="pub-download-label">
                    FULL RESEARCH REPORT
                  </span>

                  <h2>
                    Read the complete Nebraska AgTech Innovation Report
                  </h2>

                  <p>
                    Download the complete research paper including
                    methodology, ecosystem analysis, investment
                    benchmarking, SWOG analysis, figures, findings,
                    and strategic recommendations.
                  </p>

                </div>


                <a
                  href={ReportPDF}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="pub-download-main"
                >

                  <i className="fas fa-file-pdf" />

                  Download PDF

                  <i className="fas fa-arrow-down" />

                </a>

              </section>

            </article>


            {/* =================================================
                RIGHT SIDE — AUTHORS / PUBLICATION
                ================================================= */}

            <aside className="pub-sidebar">


              {/* =================================================
                  AUTHORS
                  ================================================= */}

              <div className="pub-author-box">

                <span className="pub-sidebar-label">
                  AUTHORS
                </span>


                {/* ANKIT CHANDRA */}

                <div className="pub-author-card">

                  <div className="pub-author-photo-wrap">

                    <img
                      src={AnkitPhoto}
                      alt="Ankit Chandra"
                      className="pub-author-photo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';

                        const fallback =
                          e.currentTarget.nextElementSibling;

                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />

                    <div
                      className="pub-author-photo-fallback"
                      style={{ display: 'none' }}
                    >
                      AC
                    </div>

                  </div>


                  <div className="pub-author-info">

                    <p className="pub-author-name">
                      Ankit Chandra
                    </p>

                    <p className="pub-author-role">
                      Director, Spur Ventures
                    </p>

                    <p className="pub-author-org">
                      University of Nebraska–Lincoln
                    </p>

                  </div>

                </div>


                {/* ISHANI LAL */}

                <div className="pub-author-card">

                  <div className="pub-author-photo-wrap">

                    <img
                      src={LalPhoto}
                      alt="Ishani Lal"
                      className="pub-author-photo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';

                        const fallback =
                          e.currentTarget.nextElementSibling;

                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />

                    <div
                      className="pub-author-photo-fallback"
                      style={{ display: 'none' }}
                    >
                      IL
                    </div>

                  </div>


                  <div className="pub-author-info">

                    <p className="pub-author-name">
                      Ishani Lal
                    </p>

                    <p className="pub-author-role">
                      Economist
                    </p>

                    <p className="pub-author-org">
                      Department of Agricultural Economics,
                      University of Nebraska–Lincoln
                    </p>

                  </div>

                </div>


                {/* BRENNAN COSTELLO */}

                <div className="pub-author-card">

                  <div className="pub-author-photo-wrap">

                    <img
                      src={CostelloPhoto}
                      alt="Brennan Costello"
                      className="pub-author-photo"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';

                        const fallback =
                          e.currentTarget.nextElementSibling;

                        if (fallback) {
                          fallback.style.display = 'flex';
                        }
                      }}
                    />

                    <div
                      className="pub-author-photo-fallback"
                      style={{ display: 'none' }}
                    >
                      BC
                    </div>

                  </div>


                  <div className="pub-author-info">

                    <p className="pub-author-name">
                      Brennan Costello
                    </p>

                    <p className="pub-author-role">
                      Director, The Combine
                    </p>

                    <p className="pub-author-org">
                      Nebraska
                    </p>

                  </div>

                </div>

              </div>


              {/* =================================================
                  PUBLICATION INFORMATION
                  ================================================= */}

              <div className="pub-info-box">

                <span className="pub-sidebar-label">
                  PUBLICATION
                </span>


                <div className="pub-info-row">

                  <span>
                    Published
                  </span>

                  <strong>
                    July 2026
                  </strong>

                </div>


                <div className="pub-info-row">

                  <span>
                    Type
                  </span>

                  <strong>
                    White Paper
                  </strong>

                </div>


                <div className="pub-info-row">

                  <span>
                    Series
                  </span>

                  <strong>
                    Spur Ventures Research
                  </strong>

                </div>


                <div className="pub-info-row">

                  <span>
                    Access
                  </span>

                  <strong>
                    Open Access
                  </strong>

                </div>

              </div>


              {/* =================================================
                  DOWNLOAD BUTTON
                  ================================================= */}

              <a
                href={ReportPDF}
                target="_blank"
                rel="noopener noreferrer"
                className="pub-sidebar-download"
              >

                <i className="fas fa-file-pdf" />

                <span>

                  Download Report

                  <small>
                    PDF • Full Research Paper
                  </small>

                </span>

                <i className="fas fa-arrow-down" />

              </a>

            </aside>

          </div>

        </div>

      </main>

    </div>
  );
};

export default NebraskaAgTechInnovationReport;